/**
 * ResourceLink - Renders an AWS resource ARN as a clickable link with service badge.
 *
 * RESEARCH FINDINGS: AWS Console Resource Link Styling (Cloudscape patterns)
 * ============================================================================
 *
 * 1. Link component (https://cloudscape.aws.dev/components/link/):
 *    - Use `variant="primary"` (underlined) for links adjacent to other text or in key-value pairs.
 *    - Use `variant="secondary"` where context implies interactivity (e.g., list of links).
 *    - Use `external` prop + externalIconAriaLabel for links opening in a new tab (e.g., console URLs).
 *    - Never use links for actions — use Button instead.
 *    - Provide descriptive ariaLabel for disambiguation when multiple links exist.
 *
 * 2. Key-value display (https://cloudscape.aws.dev/components/key-value-pairs/):
 *    - Use <Box variant="awsui-key-label"> for the label, value below it.
 *    - For ARNs: use CopyToClipboard (inline variant) so users can copy the full ARN.
 *    - For resource links in values: use Link with `variant="primary"` pointing to the console URL.
 *    - Use hyphen (-) for empty values, never truncate with ellipsis.
 *    - Values may contain status indicators and external link icons.
 *
 * 3. Details page pattern (https://cloudscape.aws.dev/patterns/resource-management/details/):
 *    - Place most relevant info in a "Details summary container" using key-value pairs.
 *    - Resource ARN typically shown with CopyToClipboard inline variant.
 *    - Related resource ARNs (e.g., target Lambda, SQS queue) shown as external Links
 *      pointing to that service's console page.
 *
 * 4. How AWS consoles (SQS, Lambda, EventBridge) display target ARNs:
 *    - Service badge/icon + resource name as a primary Link (external) to the target console.
 *    - Full ARN available via popover or copy-to-clipboard on hover/click.
 *    - EventBridge rules page shows targets with: [Service icon] [Resource name (link)] [ARN in popover].
 *    - SQS shows "Dead-letter queue" as a Link to the target queue's detail page.
 *    - Lambda shows "Destination" ARNs as external links with the resource name extracted.
 *
 * RECOMMENDED PATTERN for this component:
 *    - Keep the current approach: service badge + Link (external, primary variant) + Popover for full ARN.
 *    - Consider migrating to KeyValuePairs component when displaying ARN in a details container.
 *    - For inline ARN display, use CopyToClipboard (inline variant) from Cloudscape.
 *    - Ensure externalIconAriaLabel is set on external links for accessibility.
 */
import React from 'react';
import Link from '@cloudscape-design/components/link';
import Popover from '@cloudscape-design/components/popover';
import Box from '@cloudscape-design/components/box';
import { parseArn, getServiceLabel } from '../utils/arnUtils';

interface ResourceLinkProps {
  arn: string;
}

const SERVICE_COLORS: Record<string, { bg: string; text: string }> = {
  sqs: { bg: '#e6f2ff', text: '#0073bb' },
  sns: { bg: '#fce8f3', text: '#8b1a4a' },
  lambda: { bg: '#e8f5e9', text: '#1b5e20' },
  events: { bg: '#fff3e0', text: '#e65100' },
};

const badgeStyle = (service: string): React.CSSProperties => {
  const colors = SERVICE_COLORS[service] || { bg: '#f0f0f0', text: '#555' };
  return {
    display: 'inline-block',
    padding: '1px 6px',
    borderRadius: 4,
    fontSize: 11,
    fontWeight: 600,
    background: colors.bg,
    color: colors.text,
    marginRight: 6,
    verticalAlign: 'middle',
  };
};

export function ResourceLink({ arn }: ResourceLinkProps) {
  const parsed = parseArn(arn);
  if (!parsed) return <span>{arn}</span>;

  return (
    <Popover
      dismissButton={false}
      position="top"
      size="large"
      triggerType="custom"
      content={<Box fontSize="body-s" color="text-body-secondary"><code>{arn}</code></Box>}
    >
      <span style={{ display: 'inline-flex', alignItems: 'center' }}>
        <span style={badgeStyle(parsed.service)}>{getServiceLabel(parsed.service)}</span>
        <Link href={parsed.consoleUrl} external variant="primary">
          {parsed.resourceName}
        </Link>
      </span>
    </Popover>
  );
}
